import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import { ConfirmationDialogService } from '@components/base-elements/confirmation-dialog/confirmation-dialog.service';
import { PmButtonComponent, PmIconComponent } from '@libs/ui';
import {
  selectIsLoggedInUserAdmin,
  selectLoggedInUser,
} from '@core/store/auth/auth.selectors';
import { DateUtilsService } from '@core/utils/date-utils.service';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FeatureFlagSelectors,
  selectFeatureFlagById,
} from 'core/store/feature-flags/feature-flags.selectors';
import { buildConsensusDeleteWarning } from 'consensus/cycles/services/consensus-delete-warning.helper';
import { CyclesHttpClient } from 'consensus/cycles/services/cycles-http-client';
import { clusterFromEnvironment } from 'core/utils/utils';
import { CreateExportDialogComponent } from 'demand-forecasting/components/dialogs/create-export/create-export-dialog.component';
import { FilterDialogComponent } from 'demand-forecasting/components/dialogs/filter-dialog.component';
import { ForecastDetailsDialogComponent } from 'demand-forecasting/components/dialogs/forecast-details-dialog.component';
import { ExecuteConfigurationOptionsComponent } from 'demand-forecasting/components/execute-configuration-options/execute-configuration-options.component';
import { ForecastsHttpClient } from 'demand-forecasting/services/http/forecast-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import {
  selectAvailableFilters,
  selectSavedFilterCombinations,
} from 'demand-forecasting/store/filter/filter.selectors';
import {
  isForecastSeriesFetchingOrPending,
  latestCompletedForecastSeries,
} from 'demand-forecasting/store/forecast-series/forecast-series.selectors';
import { ForecastVersionActions } from 'demand-forecasting/store/forecast-version/forecast-version.actions';
import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { EditWidgetViewDialogComponent } from 'insights/components/dialogs/edit-widget-view-dialog/edit-widget-view-dialog.component';
import {
  LucideDatabaseBackup,
  LucideExternalLink,
  LucideEllipsis,
  LucideEye,
  LucideEyeOff,
  LucideGrid2X2,
  LucideInfo,
  LucideSettings,
  LucideTrash2,
} from '@lucide/angular';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, take, tap } from 'rxjs';
import { ChartOptionsDialogComponent } from '../../dialogs/chart-options/chart-options-dialog.component';
import { AdditionalTimeSeriesPickerComponent } from '../additional-time-series-picker/additional-time-series-picker.component';
import { FilterCombinationPickerComponent } from '../filter-combination-picker/filter-combination-picker.component';
import { ForecastResolutionComponent } from '../forecast-resolution/forecast-resolution.component';
import { ForecastVersionPickerComponent } from '../forecast-version-picker/forecast-version-picker.component';
import { TargetUnitPickerComponent } from '../target-unit-picker/target-unit-picker.component';

const GRAFANA_FORECAST_LOGS_URL = `${environment.grafanaUrl}${environment.forecastLogsGrafanaDashboardPath}`;

@Component({
  selector: 'pm-options-header',
  templateUrl: './options-header.component.html',
  imports: [
    TargetUnitPickerComponent,
    ForecastResolutionComponent,
    AdditionalTimeSeriesPickerComponent,
    FilterCombinationPickerComponent,
    ForecastVersionPickerComponent,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    TranslateModule,
    MatDivider,
    ExecuteConfigurationOptionsComponent,
    PmButtonComponent,
    PmIconComponent,
  ],
})
export class OptionsHeaderComponent {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly forecastClient = inject(ForecastsHttpClient);
  private readonly cyclesHttpClient = inject(CyclesHttpClient);
  private readonly dateUtilsService = inject(DateUtilsService);
  private readonly http = inject(HttpClient);

  protected readonly moreIcon = LucideEllipsis;
  protected readonly settingsIcon = LucideSettings;
  protected readonly visibilityIcon = LucideEye;
  protected readonly visibilityOffIcon = LucideEyeOff;
  protected readonly externalLinkIcon = LucideExternalLink;
  protected readonly infoIcon = LucideInfo;
  protected readonly trashIcon = LucideTrash2;
  protected readonly gridIcon = LucideGrid2X2;
  protected readonly databaseBackupIcon = LucideDatabaseBackup;

  private readonly confirmationDialogService = inject(
    ConfirmationDialogService,
  );

  protected latestForecastSeries = this.store.selectSignal(
    latestCompletedForecastSeries,
  );
  protected isLoggedInUserAdmin = this.store.selectSignal(
    selectIsLoggedInUserAdmin,
  );
  protected availableFilterCombinations = this.store.selectSignal(
    selectSavedFilterCombinations,
  );
  protected allAvailableAdditionalTimeSeriesNames = this.store.selectSignal(
    ConfigurationsSelectors.selectAdditionalTimeSeriesNames,
  );
  protected validFilters = this.store.selectSignal(selectAvailableFilters);

  protected isForecastSeriesFetching = this.store.selectSignal(
    isForecastSeriesFetchingOrPending,
  );

  private readonly currentConfiguration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );

  private readonly additionalTargetUnitNames = this.store.selectSignal(
    ConfigurationsSelectors.selectAdditionalTargetUnitNames,
  );

  protected isExportButtonEnabled = this.store.selectSignal(
    FeatureFlagSelectors.selectIsExportButtonFeatureFlagEnabled,
  );
  protected forecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );

  private readonly loggedInUser = this.store.selectSignal(selectLoggedInUser);

  /**
   * @deprecated This feature flag is only for temporary use in the Medicos Scenario Planning Light feature and will be removed in the future.
   */
  protected isTmpScenarioPlanningLightProvisionalEnabled = computed(() => {
    const flag = this.store.selectSignal(
      selectFeatureFlagById(
        FeatureFlagId.tmp_scenario_planning_light_provisional,
      ),
    )();
    return flag.enabled;
  });

  protected insightsFeature = this.store.selectSignal(
    FeatureFlagSelectors.selectInsightsFeatureFlag,
  );

  private readonly consensusFeature = this.store.selectSignal(
    FeatureFlagSelectors.selectDemandConsensusFeatureEnabled,
  );

  private readonly toastService = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  protected targetUnits = computed(() => {
    const defaultTargetUnit =
      this.currentConfiguration()?.startForecastConfig.target;
    if (defaultTargetUnit) {
      if (this.additionalTargetUnitNames().includes(defaultTargetUnit)) {
        return this.additionalTargetUnitNames();
      }
      return this.additionalTargetUnitNames().concat(defaultTargetUnit);
    }
    return [];
  });

  protected isChartOptionsEnabled = this.store.selectSignal(
    FeatureFlagSelectors.selectConfidenceIntervalFeatureFlag,
  );

  openFilterDialog() {
    this.dialog.open(FilterDialogComponent, {
      autoFocus: false,
    });
  }

  openExportDialog() {
    this.dialog.open(CreateExportDialogComponent, {
      disableClose: true,
      autoFocus: true,
    });
  }

  openChartOptionsDialog() {
    this.dialog.open(ChartOptionsDialogComponent, {
      autoFocus: false,
    });
  }

  openForecastDetailsDialog() {
    this.dialog.open(ForecastDetailsDialogComponent, {
      data: { configurationId: this.currentConfiguration()?.id },
    });
  }

  openWidgetEditorDialog() {
    this.dialog
      .open(EditWidgetViewDialogComponent, {
        data: {
          configuration: this.currentConfiguration(),
        },
      })
      .afterClosed()
      .subscribe((reload) => {
        if (reload) {
          window.location.reload();
        }
      });
  }

  openDeleteForecastVersionDialog() {
    const selectedForecastVersion = this.forecastVersion();
    if (!selectedForecastVersion) {
      return;
    }

    const consensusCycleCount$ = this.consensusFeature().enabled
      ? this.cyclesHttpClient
          .countActiveCyclesByForecastVersionId(selectedForecastVersion.id)
          .pipe(
            take(1),
            catchError(() => of(0)),
          )
      : of(0);

    consensusCycleCount$.subscribe((consensusCycleCount) => {
      const formattedCreatedAt =
        this.dateUtilsService.formatDateBasedOnLanguage(
          selectedForecastVersion.createdAt,
        );

      this.confirmationDialogService.launchDialog({
        variant: 'delete',
        headline: this.translate.instant(
          'RESULT_OVERVIEW.VERSION_PICKER.MODAL_HINT',
          {
            createdAt: formattedCreatedAt,
          },
        ),
        ...buildConsensusDeleteWarning(
          consensusCycleCount,
          'CONSENSUS.DELETE_WARNING.VERSION_DESCRIPTION',
        ),
        onApprove: () => {
          this.store.dispatch(
            ForecastVersionActions.deleteForecastVersion({
              versionId: selectedForecastVersion.id,
            }),
          );
        },
      });
    });
  }

  openForecastLogs() {
    const forecastVersion = this.forecastVersion();
    const company = this.loggedInUser()?.company;
    if (!forecastVersion || !company) {
      console.error('Cannot open logs: missing forecast version or company');
      return;
    }
    const cluster = clusterFromEnvironment();
    const companySlug = company
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-{2,}/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 40);
    const params = new URLSearchParams({
      'var-cluster': cluster,
      'var-customer': companySlug,
      'var-forecast_id': forecastVersion.forecastId,
      from: String(new Date(forecastVersion.createdAt).getTime()),
      to: 'now',
    });
    const url = `${GRAFANA_FORECAST_LOGS_URL}?${params.toString()}`;
    window.open(url, '_blank', 'noopener');
  }

  updateForecastVersionVisibility(
    isVisible: boolean,
    forecastVersionId: string,
  ) {
    this.store.dispatch(
      ForecastVersionActions.updateForecastVersion({
        forecastVersionId,
        updateForecastVersionDto: { isVisible },
      }),
    );
  }

  /**
   * @deprecated This method is only for temporary use in the Medicos Scenario Planning Light feature and will be removed in the future.
   */
  handleTmpScenarioPlanningLightProvisional() {
    this.http
      .post(
        `${environment.api.url}/medicos-scenario-light-planning/configurations/${this.configurationId}`,
        {},
      )
      .pipe(
        tap(() =>
          this.toastService.success(
            this.translate.instant('MEDICOS.ETL_SUBMISSION_ACCEPTED'),
          ),
        ),
        catchError((err) => {
          if (err instanceof HttpErrorResponse && err.status === 409) {
            this.toastService.error(
              this.translate.instant('MEDICOS.ETL_IS_ALREADY_RUNNING'),
            );
          }
          return err;
        }),
      )
      .subscribe();
  }

  get configurationId() {
    const currentConfiguration = this.currentConfiguration();
    if (currentConfiguration) {
      return currentConfiguration.id;
    }
    return '';
  }

  get numberOfVersions(): number {
    const currentConfiguration = this.currentConfiguration();
    if (currentConfiguration) {
      return currentConfiguration.numberOfVersions;
    }
    return 0;
  }
}
